import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Zap, ChevronLeft, ChevronRight, Brain, Highlighter, CheckCircle2,
  XCircle, ArrowLeft, Trophy, Flame, BookOpen, PartyPopper, Gauge,
  ZoomIn, ZoomOut, Maximize2, Timer, StickyNote, Map, PenLine
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import NotesPanel from "@/components/NotesPanel";
import AddToCollection from "@/components/AddToCollection";
import ReadingTimer from "@/components/ReadingTimer";
import FloatingHighlight from "@/components/FloatingHighlight";
import FloatingNote from "@/components/FloatingNote";
import SQ3RFlow from "@/components/SQ3RFlow";
import RoadmapView from "@/components/RoadmapView";
import ReflectionJournal from "@/components/ReflectionJournal";

// Set up PDF.js worker via CDN (import.meta.url breaks in CRA webpack)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const HIGHLIGHT_COLORS = [
  { label: "Purple", value: "#C4B5FD", class: "bg-[#C4B5FD]" },
  { label: "Yellow", value: "#FDE047", class: "bg-[#FDE047]" },
  { label: "Green", value: "#86EFAC", class: "bg-[#86EFAC]" },
  { label: "Blue", value: "#7DD3FC", class: "bg-[#7DD3FC]" },
];

export default function Reader({ api }) {
  const { paperId } = useParams();
  const navigate = useNavigate();
  const pdfContainerRef = useRef(null);

  const [paper, setPaper] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfFailed, setPdfFailed] = useState(false);
  const [pdfRetrying, setPdfRetrying] = useState(false);
  const [pageText, setPageText] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [highlights, setHighlights] = useState([]);
  const [highlightColor, setHighlightColor] = useState(HIGHLIGHT_COLORS[0].value);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [pagesReadThisSession, setPagesReadThisSession] = useState(0);
  const [pagesSinceLastQuiz, setPagesSinceLastQuiz] = useState(0);
  const [showXpPopup, setShowXpPopup] = useState(false);
  const [xpAmount, setXpAmount] = useState(0);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [quizDifficulty, setQuizDifficulty] = useState("medium");
  const [pdfScale, setPdfScale] = useState(1.0);
  const [containerWidth, setContainerWidth] = useState(800);
  const [showTools, setShowTools] = useState(false);
  const [readerView, setReaderView] = useState("loading"); // "sq3r" | "reading" | "reflection"
  const [sq3rStep, setSq3rStep] = useState("survey");
  const [showRoadmap, setShowRoadmap] = useState(false);
  const [showReflection, setShowReflection] = useState(false);

  // Load paper metadata
  useEffect(() => {
    const fetchPaper = async () => {
      try {
        const res = await axios.get(`${api}/papers/${paperId}`, { withCredentials: true });
        setPaper(res.data);
        setCurrentPage(res.data.current_page || 0);
        const hlRes = await axios.get(`${api}/highlights/${paperId}`, { withCredentials: true });
        setHighlights(hlRes.data);
      } catch (e) {
        toast.error("Failed to load paper");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchPaper();
  }, [api, paperId, navigate]);

  // Determine initial view based on SQ3R progress
  useEffect(() => {
    if (!paper) return;
    const checkSq3r = async () => {
      try {
        const res = await axios.get(`${api}/papers/${paperId}/sq3r`, { withCredentials: true });
        const step = res.data?.current_step;
        if (step === "read" || step === "recite" || step === "review" || step === "complete") {
          setReaderView("reading");
          setSq3rStep(step);
        } else {
          setReaderView("sq3r");
          setSq3rStep(step || "survey");
        }
      } catch {
        setReaderView("reading");
      }
    };
    checkSq3r();
  }, [api, paperId, paper]);

  // Load PDF blob with retry
  useEffect(() => {
    if (!paper) return;
    let revoke = null;
    const fetchPdf = async () => {
      setPdfFailed(false);
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const res = await axios.get(`${api}/papers/${paperId}/pdf`, {
            withCredentials: true,
            responseType: "blob",
          });
          const blobUrl = URL.createObjectURL(res.data);
          setPdfUrl(blobUrl);
          revoke = blobUrl;
          return;
        } catch (e) {
          if (attempt < 2) {
            await new Promise((r) => setTimeout(r, 1500));
          }
        }
      }
      // All retries failed — fall back to text mode
      setPdfFailed(true);
    };
    fetchPdf();
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [api, paperId, paper]);

  // Load page text (for text fallback, and always available for quizzes)
  useEffect(() => {
    if (!paper) return;
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${api}/papers/${paperId}/page/${currentPage}`, { withCredentials: true });
        setPageText(res.data.text);
      } catch {
        setPageText("");
      }
    };
    fetchPage();
  }, [api, paperId, currentPage, paper]);

  const retryPdfLoad = async () => {
    setPdfRetrying(true);
    setPdfFailed(false);
    setPdfUrl(null);
    try {
      const res = await axios.get(`${api}/papers/${paperId}/pdf`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      setPdfUrl(blobUrl);
    } catch {
      setPdfFailed(true);
    } finally {
      setPdfRetrying(false);
    }
  };

  // Measure container width for responsive PDF scaling
  useEffect(() => {
    const measure = () => {
      if (pdfContainerRef.current) {
        // Measure the actual available width inside the card, minus padding on both sides
        const rect = pdfContainerRef.current.getBoundingClientRect();
        // Card has p-4 (16px each side) = 32px, viewer border = 4px, scrollbar ~17px, safety margin
        const w = rect.width - 90;
        setContainerWidth(Math.max(w, 250));
      }
    };
    measure();
    // Re-measure on resize and orientation change
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", () => setTimeout(measure, 200));
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [loading]);

  const onDocumentLoadSuccess = ({ numPages: n }) => {
    setNumPages(n);
  };

  const goToPage = async (pageNum) => {
    const maxPage = (numPages || paper?.total_pages || 1) - 1;
    if (pageNum < 0 || pageNum > maxPage) return;
    const isForward = pageNum > currentPage;
    setCurrentPage(pageNum);

    if (isForward) {
      setPagesReadThisSession((prev) => prev + 1);
      setPagesSinceLastQuiz((prev) => prev + 1);

      try {
        const res = await axios.post(`${api}/papers/${paperId}/progress?page_num=${pageNum}`, {}, { withCredentials: true });
        if (res.data.paper_status === "completed") {
          handleCompletion();
        }
      } catch (e) {
        // ignore
      }

      if (pagesSinceLastQuiz >= 2) {
        triggerQuiz(pageNum);
      }
    }
  };

  const triggerQuiz = async (endPage) => {
    setQuizLoading(true);
    setShowQuiz(true);
    setQuizSubmitted(false);
    setQuizResult(null);
    setSelectedAnswers({});
    const startPage = Math.max(0, endPage - 2);
    try {
      const res = await axios.get(
        `${api}/papers/${paperId}/quiz?start_page=${startPage}&end_page=${endPage}&difficulty=${quizDifficulty}`,
        { withCredentials: true }
      );
      setQuiz(res.data);
    } catch (e) {
      toast.error("Failed to generate quiz");
      setShowQuiz(false);
    } finally {
      setQuizLoading(false);
    }
    setPagesSinceLastQuiz(0);
  };

  const submitQuiz = async () => {
    if (!quiz) return;
    const correctAnswers = [];
    quiz.questions.forEach((q, i) => {
      if (selectedAnswers[i] === q.correct_index) {
        correctAnswers.push(selectedAnswers[i]);
      }
    });

    try {
      const res = await axios.post(`${api}/papers/${paperId}/quiz/submit`, {
        paper_id: paperId,
        answers: correctAnswers,
        page_range: quiz.page_range,
        pages_read_this_session: pagesReadThisSession,
        difficulty: quizDifficulty,
      }, { withCredentials: true });
      setQuizResult(res.data);
      setQuizSubmitted(true);
      setXpAmount(res.data.xp_earned);
      setShowXpPopup(true);
      setTimeout(() => setShowXpPopup(false), 2000);

      if (res.data.new_badges?.length > 0) {
        res.data.new_badges.forEach((badge) => {
          toast.success(`Badge unlocked: ${badge}!`);
        });
      }

      // Save quiz questions as review cards for spaced repetition
      try {
        await axios.post(`${api}/reviews/save`, {
          paper_id: paperId,
          questions: quiz.questions,
        }, { withCredentials: true });
      } catch {
        // Non-blocking - review cards are a bonus
      }
    } catch (e) {
      toast.error("Failed to submit quiz");
    }
  };

  const handleHighlight = async () => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();
    if (!text || text.length < 3) {
      toast.error("Select some text on the PDF to highlight");
      return;
    }
    try {
      const res = await axios.post(`${api}/highlights/json`, {
        paper_id: paperId,
        page_number: currentPage,
        text: text,
        color: highlightColor,
      }, { withCredentials: true });
      setHighlights((prev) => [...prev, res.data]);
      toast.success("Highlight saved!");
      selection.removeAllRanges();
    } catch (e) {
      toast.error("Failed to save highlight");
    }
  };

  const handleCompletion = async () => {
    try {
      const res = await axios.post(`${api}/papers/${paperId}/complete`, {}, { withCredentials: true });
      const statsRes = await axios.get(`${api}/stats`, { withCredentials: true });
      setCompletionData({ ...res.data, stats: statsRes.data, paper });
      setShowCompletionModal(true);
      setShowReflection(true);
    } catch (e) {
      // ignore
    }
  };

  const pageHighlights = highlights.filter((h) => h.page_number === currentPage);
  const totalPages = numPages || paper?.total_pages || 1;
  const progress = ((currentPage + 1) / totalPages) * 100;

  // Custom text renderer for highlighting saved text
  const customTextRenderer = useCallback(
    (textItem) => {
      const hl = pageHighlights.find((h) => textItem.str && h.text.includes(textItem.str) && textItem.str.length > 3);
      if (hl) {
        return `<mark style="background-color: ${hl.color}40; border-radius: 2px; padding: 0 2px;">${textItem.str}</mark>`;
      }
      return textItem.str;
    },
    [pageHighlights]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="reader-loading">
        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] text-center p-8">
          <div className="w-12 h-12 border-4 border-neo-yellow border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-heading font-bold text-lg dark:text-white">Loading paper...</p>
        </div>
      </div>
    );
  }

  if (!paper) return null;

  return (
    <div data-testid="reader-page">
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/")}
            className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white flex items-center gap-2"
            data-testid="reader-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <h2 className="font-heading font-bold text-base sm:text-lg truncate max-w-[200px] sm:max-w-md dark:text-white">{paper.title}</h2>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Zoom Controls */}
          <button
            onClick={() => setPdfScale((s) => Math.max(0.4, s - 0.2))}
            className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white p-1.5"
            data-testid="zoom-out-btn"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-xs font-bold px-1 dark:text-white min-w-[36px] text-center">{Math.round(pdfScale * 100)}%</span>
          <button
            onClick={() => setPdfScale((s) => Math.min(4, s + 0.2))}
            className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white p-1.5"
            data-testid="zoom-in-btn"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={() => setPdfScale(1.0)}
            className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white p-1.5"
            data-testid="zoom-reset-btn"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <div className="neo-badge bg-neo-yellow text-xs">
            <Zap className="w-3 h-3" />
            <span data-testid="reader-xp">{paper.xp_earned} XP</span>
          </div>
          <div className="neo-badge bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white text-xs">
            <BookOpen className="w-3 h-3" />
            <span data-testid="reader-page-count">{currentPage + 1}/{totalPages}</span>
          </div>
          <AddToCollection api={api} paperId={paperId} />
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="neo-progress" style={{ height: "10px" }}>
          <div className="neo-progress-fill" style={{ width: `${progress}%`, height: "100%" }} data-testid="reader-progress-bar" />
        </div>
      </div>

      {/* SQ3R Pre-Reading Flow */}
      {readerView === "sq3r" && (
        <div className="mb-4" data-testid="sq3r-panel">
          <SQ3RFlow
            api={api}
            paperId={paperId}
            currentStep={sq3rStep}
            onStepChange={(step) => {
              setSq3rStep(step);
              if (step === "read") setReaderView("reading");
            }}
            onStartReading={() => setReaderView("reading")}
          />
        </div>
      )}

      {/* Roadmap Toggle */}
      {readerView === "reading" && (
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowRoadmap(!showRoadmap)}
            className={`neo-btn text-xs flex items-center gap-1.5 ${showRoadmap ? "neo-btn-purple" : "bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white"}`}
            data-testid="toggle-roadmap-btn"
          >
            <Map className="w-3.5 h-3.5" />
            {showRoadmap ? "Hide Roadmap" : "AI Roadmap"}
          </button>
          {sq3rStep !== "survey" && sq3rStep !== "question" && (
            <button
              onClick={() => setReaderView("sq3r")}
              className="neo-btn text-xs bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white flex items-center gap-1.5"
              data-testid="back-to-sq3r-btn"
            >
              <BookOpen className="w-3.5 h-3.5" /> SQ3R Notes
            </button>
          )}
          <button
            onClick={() => setShowReflection(!showReflection)}
            className={`neo-btn text-xs flex items-center gap-1.5 ${showReflection ? "neo-btn-purple" : "bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white"}`}
            data-testid="toggle-reflection-btn"
          >
            <PenLine className="w-3.5 h-3.5" />
            {showReflection ? "Hide Journal" : "Reflection Journal"}
          </button>
        </div>
      )}

      {/* Roadmap View (collapsible) */}
      {showRoadmap && readerView === "reading" && (
        <div className="mb-3" data-testid="roadmap-panel">
          <RoadmapView api={api} paperId={paperId} />
        </div>
      )}

      {/* Compact Tools Bar */}
      {readerView === "reading" && (
      <div className="mb-3">
        <button
          onClick={() => setShowTools(!showTools)}
          className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white text-xs py-1.5 px-3 flex items-center gap-2 w-full justify-between"
          data-testid="toggle-tools-btn"
        >
          <span className="flex items-center gap-2 md:gap-3 flex-wrap">
            <span className="flex items-center gap-1"><Timer className="w-3.5 h-3.5 text-neo-green" /> <span className="hidden sm:inline">Timer</span></span>
            <span className="flex items-center gap-1"><Gauge className="w-3.5 h-3.5 text-neo-purple" /> {quizDifficulty.charAt(0).toUpperCase() + quizDifficulty.slice(1)}</span>
            <span className="flex items-center gap-1"><Highlighter className="w-3.5 h-3.5 text-neo-purple" /> <span className="hidden sm:inline">Highlight</span></span>
            <span className="flex items-center gap-1"><StickyNote className="w-3.5 h-3.5 text-neo-yellow" /> <span className="hidden sm:inline">Notes</span></span>
            <span className="flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-neo-yellow" /> {pagesReadThisSession}p</span>
          </span>
          <ChevronRight className={`w-4 h-4 transition-transform ${showTools ? "rotate-90" : ""}`} />
        </button>

        {showTools && (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {/* Timer */}
            <ReadingTimer api={api} paperId={paperId} pagesRead={pagesReadThisSession} />

            {/* Quiz Difficulty + Speed Bonus combined */}
            <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] p-4" data-testid="quiz-difficulty-card">
              <div className="flex items-center gap-2 mb-2">
                <Gauge className="w-4 h-4 text-neo-purple" />
                <h3 className="font-heading font-bold text-sm dark:text-white">Quiz Difficulty</h3>
              </div>
              <Select value={quizDifficulty} onValueChange={setQuizDifficulty}>
                <SelectTrigger className="border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg text-sm h-9" data-testid="difficulty-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy" data-testid="difficulty-easy">Easy (1x XP)</SelectItem>
                  <SelectItem value="medium" data-testid="difficulty-medium">Medium (1.5x XP)</SelectItem>
                  <SelectItem value="hard" data-testid="difficulty-hard">Hard (2x XP)</SelectItem>
                </SelectContent>
              </Select>
              <div className="mt-2 text-xs text-neo-text-secondary dark:text-gray-400">
                Speed: <span className={pagesReadThisSession >= 2 ? "text-neo-green font-bold" : ""}>2p+20</span> / <span className={pagesReadThisSession >= 3 ? "text-neo-green font-bold" : ""}>3p+50</span> / <span className={pagesReadThisSession >= 5 ? "text-neo-green font-bold" : ""}>5p+100 XP</span>
              </div>
            </div>

            {/* Highlight Tool compact */}
            <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] p-4" data-testid="highlight-tool">
              <div className="flex items-center gap-2 mb-2">
                <Highlighter className="w-4 h-4 text-neo-purple" />
                <h3 className="font-heading font-bold text-sm dark:text-white">Highlight</h3>
              </div>
              <div className="flex items-center gap-2 mb-2">
                {HIGHLIGHT_COLORS.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => setHighlightColor(c.value)}
                    className={`w-7 h-7 rounded-full border-2 ${
                      highlightColor === c.value ? "border-neo-border dark:border-white shadow-[2px_2px_0px_0px_#1A1A1A]" : "border-gray-300 dark:border-gray-600"
                    } ${c.class}`}
                    data-testid={`highlight-color-${c.label.toLowerCase()}`}
                  />
                ))}
              </div>
              <button
                onClick={handleHighlight}
                className="neo-btn neo-btn-purple w-full flex items-center justify-center gap-1 text-xs py-1.5"
                data-testid="save-highlight-btn"
              >
                <Highlighter className="w-3 h-3" /> Save Highlight
              </button>
              {pageHighlights.length > 0 && (
                <div className="mt-2 max-h-[80px] overflow-y-auto space-y-1">
                  {pageHighlights.map((h) => (
                    <div key={h.id} className="text-xs p-1.5 rounded border border-neo-border/30 dark:border-[#444]/50 dark:text-gray-300" style={{ backgroundColor: `${h.color}20` }} data-testid={`highlight-${h.id}`}>
                      "{h.text.substring(0, 60)}{h.text.length > 60 ? "..." : ""}"
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes compact */}
            <NotesPanel api={api} paperId={paperId} currentPage={currentPage} />
          </div>
        )}
      </div>
      )} {/* end readerView === "reading" tools bar */}

      {/* PDF Reading Area — FULL WIDTH */}
      {readerView === "reading" && (
      <div ref={pdfContainerRef}>
        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] p-3" data-testid="reader-content-area">

            {/* PDF Viewer */}
            <div
              className="overflow-auto bg-gray-100 dark:bg-[#2A2A2A] rounded-lg border-2 border-neo-border/20 dark:border-[#444]/40"
              style={{ maxHeight: "78vh" }}
              data-testid="pdf-viewer"
            >
              {pdfFailed ? (
                /* Text Fallback when PDF can't load */
                <div className="p-6" data-testid="text-fallback-viewer">
                  <div className="flex items-center justify-between mb-4 p-3 rounded-lg border-2 border-neo-yellow bg-neo-yellow/10">
                    <p className="text-sm dark:text-white">
                      <span className="font-bold">PDF view unavailable</span> — showing extracted text instead.
                    </p>
                    <button
                      onClick={retryPdfLoad}
                      disabled={pdfRetrying}
                      className="neo-btn text-xs neo-btn-primary py-1 px-3"
                      data-testid="retry-pdf-btn"
                    >
                      {pdfRetrying ? "Retrying..." : "Retry PDF"}
                    </button>
                  </div>
                  <div className="prose prose-sm max-w-none dark:prose-invert">
                    {pageText ? (
                      pageText.split('\n').filter((p) => p.trim()).map((para, i) => {
                        const isHighlighted = pageHighlights.some(
                          (h) => para.includes(h.text) || h.text.includes(para.substring(0, 30))
                        );
                        return (
                          <p
                            key={i}
                            className={`mb-4 leading-relaxed text-base dark:text-gray-200 ${
                              isHighlighted ? "bg-[#C4B5FD]/30 rounded px-2 py-1" : ""
                            }`}
                          >
                            {para}
                          </p>
                        );
                      })
                    ) : (
                      <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-4 border-neo-yellow border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                </div>
              ) : pdfUrl ? (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={() => setPdfFailed(true)}
                  loading={
                    <div className="flex items-center justify-center py-20">
                      <div className="w-10 h-10 border-4 border-neo-yellow border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  }
                  error={
                    <div className="text-center py-12 px-4" data-testid="pdf-error-fallback">
                      <p className="font-heading font-bold dark:text-white mb-2">PDF couldn't render</p>
                      <p className="text-sm text-neo-text-secondary dark:text-gray-400 mb-4">Switching to text view...</p>
                      <button
                        onClick={() => setPdfFailed(true)}
                        className="neo-btn neo-btn-primary"
                        data-testid="switch-to-text-btn"
                      >
                        View as Text
                      </button>
                    </div>
                  }
                >
                  <div className="flex justify-center py-4">
                    <Page
                      pageNumber={currentPage + 1}
                      width={Math.floor(containerWidth * pdfScale)}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      customTextRenderer={customTextRenderer}
                      loading={
                        <div className="flex items-center justify-center py-20">
                          <div className="w-8 h-8 border-4 border-neo-yellow border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      }
                    />
                  </div>
                </Document>
              ) : (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="w-10 h-10 border-4 border-neo-yellow border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-sm text-neo-text-secondary dark:text-gray-400">Loading PDF...</p>
                  </div>
                </div>
              )}
            </div>

            {/* Page Navigation */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t-2 border-neo-border dark:border-[#444]">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 0}
                className={`neo-btn flex items-center gap-2 ${
                  currentPage === 0 ? "bg-gray-200 dark:bg-gray-700 text-gray-400 border-gray-300 dark:border-gray-600 shadow-none cursor-not-allowed" : "bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white"
                }`}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              {currentPage >= totalPages - 1 && paper.status !== "completed" ? (
                <button
                  onClick={handleCompletion}
                  className="neo-btn neo-btn-success flex items-center gap-2 text-lg"
                  data-testid="complete-paper-btn"
                >
                  <Trophy className="w-5 h-5" />
                  Complete Paper!
                </button>
              ) : (
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1}
                  className={`neo-btn flex items-center gap-2 ${
                    currentPage >= totalPages - 1 ? "bg-gray-200 dark:bg-gray-700 text-gray-400 border-gray-300 dark:border-gray-600 shadow-none cursor-not-allowed" : "neo-btn-primary"
                  }`}
                  data-testid="next-page-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )} {/* end readerView === "reading" PDF area */}

      {/* Reflection Journal (toggled or post-completion) */}
      {showReflection && readerView === "reading" && (
        <div className="mt-4" data-testid="reflection-panel">
          <ReflectionJournal api={api} paperId={paperId} onClose={() => setShowReflection(false)} />
        </div>
      )}

      {/* XP Popup */}
      <AnimatePresence>
        {showXpPopup && (
          <motion.div
            initial={{ opacity: 0, y: 0, scale: 0.5 }}
            animate={{ opacity: 1, y: -30, scale: 1.2 }}
            exit={{ opacity: 0, y: -60, scale: 1 }}
            className="fixed top-1/3 left-1/2 -translate-x-1/2 z-50"
            data-testid="xp-popup"
          >
            <div className="neo-card bg-neo-yellow text-center px-8 py-4">
              <p className="font-heading font-extrabold text-3xl">+{xpAmount} XP</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quiz Modal */}
      <Dialog open={showQuiz} onOpenChange={setShowQuiz}>
        <DialogContent className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[6px_6px_0px_0px_#444] border-[3px] border-neo-border shadow-[6px_6px_0px_0px_#1A1A1A] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="quiz-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-extrabold text-2xl flex items-center gap-2 dark:text-white">
              <Brain className="w-6 h-6 text-neo-purple" />
              Comprehension Check
              <span className="neo-badge text-xs bg-neo-purple ml-2">{quizDifficulty}</span>
            </DialogTitle>
          </DialogHeader>

          {quizLoading ? (
            <div className="text-center py-8" data-testid="quiz-loading">
              <div className="w-10 h-10 border-4 border-neo-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-neo-text-secondary dark:text-gray-400">Generating {quizDifficulty} questions...</p>
            </div>
          ) : quiz ? (
            <div className="space-y-6">
              {!quizSubmitted && quiz.questions.map((q, qi) => (
                <div key={qi} className="space-y-3" data-testid={`quiz-question-${qi}`}>
                  <p className="font-heading font-bold text-base dark:text-white">{qi + 1}. {q.question}</p>
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => (
                      <button
                        key={oi}
                        onClick={() => setSelectedAnswers((prev) => ({ ...prev, [qi]: oi }))}
                        className={`w-full text-left p-3 rounded-lg border-2 transition-all dark:text-gray-200 ${
                          selectedAnswers[qi] === oi
                            ? "border-neo-yellow bg-neo-yellow/20 shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444]"
                            : "border-neo-border dark:border-[#444] hover:border-neo-yellow/50"
                        }`}
                        data-testid={`quiz-option-${qi}-${oi}`}
                      >
                        <span className="text-sm">{opt}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {quizSubmitted && quizResult && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="space-y-4"
                  data-testid="quiz-results"
                >
                  {quiz.questions.map((q, qi) => (
                    <div key={qi} className="space-y-2">
                      <p className="font-heading font-bold text-base flex items-center gap-2 dark:text-white">
                        {selectedAnswers[qi] === q.correct_index ? (
                          <CheckCircle2 className="w-5 h-5 text-neo-green flex-shrink-0" />
                        ) : (
                          <XCircle className="w-5 h-5 text-neo-red flex-shrink-0" />
                        )}
                        {qi + 1}. {q.question}
                      </p>
                      <div className="space-y-1">
                        {q.options.map((opt, oi) => (
                          <div
                            key={oi}
                            className={`p-2 rounded-lg border-2 text-sm dark:text-gray-200 ${
                              oi === q.correct_index
                                ? "border-neo-green bg-neo-green/20"
                                : selectedAnswers[qi] === oi
                                ? "border-neo-red bg-neo-red/20"
                                : "border-gray-200 dark:border-gray-600"
                            }`}
                          >
                            {opt}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="neo-card bg-neo-yellow/10 border-2 border-neo-yellow dark:bg-neo-yellow/5">
                    <p className="font-heading font-extrabold text-xl mb-2 dark:text-white">
                      +{quizResult.xp_earned} XP Earned!
                    </p>
                    <div className="space-y-1 text-sm dark:text-gray-300">
                      <div className="flex justify-between">
                        <span>Correct answers ({quizResult.correct}/{quizResult.total})</span>
                        <span className="font-bold">{quizResult.correct * 50} base XP</span>
                      </div>
                      {quizDifficulty !== "easy" && (
                        <div className="flex justify-between text-neo-purple">
                          <span>Difficulty bonus ({quizDifficulty})</span>
                          <span className="font-bold">{quizDifficulty === "hard" ? "2x" : "1.5x"} multiplier</span>
                        </div>
                      )}
                      {quizResult.speed_bonus > 0 && (
                        <div className="flex justify-between text-neo-green">
                          <span>Speed bonus</span>
                          <span className="font-bold">+{quizResult.speed_bonus} XP</span>
                        </div>
                      )}
                      {quizResult.streak_bonus > 0 && (
                        <div className="flex justify-between text-neo-red">
                          <span>Streak bonus</span>
                          <span className="font-bold">+{quizResult.streak_bonus} XP</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {!quizSubmitted ? (
                <button
                  onClick={submitQuiz}
                  disabled={Object.keys(selectedAnswers).length < (quiz?.questions?.length || 0)}
                  className={`neo-btn w-full py-3 ${
                    Object.keys(selectedAnswers).length < (quiz?.questions?.length || 0)
                      ? "bg-gray-200 text-gray-500 border-gray-300 shadow-none cursor-not-allowed"
                      : "neo-btn-success"
                  }`}
                  data-testid="quiz-submit-btn"
                >
                  Submit Answers
                </button>
              ) : (
                <button
                  onClick={() => setShowQuiz(false)}
                  className="neo-btn neo-btn-primary w-full py-3"
                  data-testid="quiz-continue-btn"
                >
                  Continue Reading
                </button>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Completion Modal */}
      <Dialog open={showCompletionModal} onOpenChange={setShowCompletionModal}>
        <DialogContent className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[6px_6px_0px_0px_#444] border-[3px] border-neo-border shadow-[6px_6px_0px_0px_#1A1A1A] max-w-md" data-testid="completion-modal">
          <div className="text-center py-4">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 12 }}
            >
              <div className="w-20 h-20 rounded-full bg-neo-green border-3 border-neo-border shadow-[3px_3px_0px_0px_#1A1A1A] flex items-center justify-center mx-auto mb-4">
                <PartyPopper className="w-10 h-10 text-neo-text" />
              </div>
            </motion.div>
            <h2 className="font-heading font-extrabold text-3xl mb-2 dark:text-white">Paper Complete!</h2>
            <p className="text-neo-text-secondary dark:text-gray-400 mb-6">
              You finished reading <span className="font-bold text-neo-text dark:text-white">{paper?.title}</span>
            </p>
            {completionData && completionData.new_badges?.length > 0 && (
              <div className="neo-card bg-neo-yellow/10 border-2 border-neo-yellow mb-6">
                <p className="font-heading font-bold dark:text-white">New Badges!</p>
                <div className="flex flex-wrap gap-2 mt-2 justify-center">
                  {completionData.new_badges.map((b) => (
                    <span key={b} className="neo-badge bg-neo-yellow text-sm">{b}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={() => { setShowCompletionModal(false); setShowReflection(true); }}
                className="neo-btn neo-btn-purple flex items-center gap-2"
                data-testid="completion-reflect-btn"
              >
                <PenLine className="w-4 h-4" />
                Reflect
              </button>
              <button
                onClick={() => navigate("/reading-log")}
                className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white flex items-center gap-2"
                data-testid="completion-view-log-btn"
              >
                <BookOpen className="w-4 h-4" />
                Reading Log
              </button>
              <button
                onClick={() => navigate("/")}
                className="neo-btn neo-btn-primary flex items-center gap-2"
                data-testid="completion-dashboard-btn"
              >
                <Trophy className="w-4 h-4" />
                Dashboard
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating Highlight - appears on text selection */}
      <FloatingHighlight
        api={api}
        paperId={paperId}
        currentPage={currentPage}
        onHighlightSaved={(h) => setHighlights((prev) => [...prev, h])}
      />

      {/* Floating Note Button */}
      <FloatingNote
        api={api}
        paperId={paperId}
        currentPage={currentPage}
      />
    </div>
  );
}
